import {
    type AgentNoteItemRead,
    type AgentNoteRead,
} from '@libraries/typescript/api_client';

/**
 * One line of copy answering "what changed?" for a routed note item, plus a
 * link to the record that changed.
 *
 * Everything here is written from the scheduler's side of the glass. The
 * pipeline's own vocabulary — saved, pending, classified, applied, draft,
 * fallback, unroutable, domain names — never reaches the screen: it describes
 * our machinery, not their agency's data.
 */
export type NoteOutcome = {
    readonly itemId: string;
    readonly text: string;
    // The domain decides which record the chip previews when expanded.
    readonly domain: AgentNoteItemRead['domain'];
    // The row the processor wrote, when it wrote one. Deep links that open a
    // specific record rather than its index page need it.
    readonly resultRefId: string | null;
    // What the item actually said, carried so a consumer never has to look the
    // item back up on the note it came from.
    readonly detail: string;
    // Null for org-wide changes or when the affected entity is unknown.
    readonly entity: NoteOutcomeEntity | null;
    readonly tone: 'done' | 'needs-you';
};

export type NoteOutcomeEntity = {
    readonly type: 'caregiver' | 'client';
    readonly id: string;
    readonly name: string;
};

// A total mapping makes new domains a type error instead of blank transcript copy.
const APPLIED_TEXT_BY_DOMAIN: Record<AgentNoteItemRead['domain'], string> = {
    availability: 'Availability updated',
    memory: 'Memory updated',
    client_caregiver_preference: 'Preferences updated',
    // Attribute has no store of its own yet — it writes a memory fragment —
    // so it says where it actually landed.
    attribute: 'Memory updated',
    playbook_rule: 'Playbook updated',
    recommendation_default: 'Matching defaults updated',
    agency_context: 'Agency details updated',
    outreach_filter: 'Outreach settings updated',
};
const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The subject as a badge-able entity. Prefers the item's own extracted
 * reference when it carries a real id — one note can fan out across several
 * people — and falls back to the note-level entity the tool resolved.
 */
function subjectEntity(
    note: AgentNoteRead,
    item: AgentNoteItemRead,
): NoteOutcomeEntity | null {
    for (const ref of item.entity_refs) {
        const type = ref.entity_type.toLowerCase();
        const kind = type.includes('caregiver')
            ? 'caregiver'
            : type.includes('client')
              ? 'client'
              : null;
        // id_hint is LLM-extracted free text; only trust it as an id when it
        // actually is one.
        if (kind && ref.id_hint && UUID_PATTERN.test(ref.id_hint)) {
            return { type: kind, id: ref.id_hint, name: ref.name };
        }
    }
    if (note.caregiver) {
        return {
            type: 'caregiver',
            id: note.caregiver.id,
            name: note.caregiver.name,
        };
    }
    if (note.client) {
        return { type: 'client', id: note.client.id, name: note.client.name };
    }
    return null;
}

/**
 * Resolve one item into its outcome line, or null when there is nothing
 * truthful and useful to say yet.
 *
 * Returning null is the honest default: an item still routing, one that could
 * not be placed, and one whose processing failed all have the same meaning to
 * a scheduler — Phoebe took the note, nothing has changed yet. The chip then
 * renders bare, exactly as it does today, with no status line and nothing
 * spinning.
 */
export function noteItemOutcome(
    note: AgentNoteRead,
    item: AgentNoteItemRead,
): NoteOutcome | null {
    // A rule the org's list mode blocked: nothing was written anywhere,
    // and the coordinator must hear that. Checked before the failed-status
    // early return, because this is exactly a FAILED item that has
    // something truthful to say.
    if (item.unenforced_notice) {
        return {
            itemId: item.id,
            text: item.unenforced_notice,
            tone: 'needs-you',
            domain: item.domain,
            entity: subjectEntity(note, item),
            resultRefId: null,
            detail: item.extracted_content,
        };
    }
    if (item.status === 'pending' || item.status === 'failed') {
        return null;
    }
    const base = {
        itemId: item.id,
        resultRefId: item.result_ref_id,
        detail: item.extracted_content,
        domain: item.domain,
        entity: subjectEntity(note, item),
    } as const;
    switch (item.outcome) {
        case 'draft_created':
            return null;
        case 'applied':
            // A demotion that still "applied" landed somewhere other than the
            // domain the note asked for, so it cannot claim that update — and
            // a note quietly kept as background context is not worth a line.
            if (item.requested_domain !== item.domain) {
                return null;
            }
            return {
                ...base,
                text: APPLIED_TEXT_BY_DOMAIN[item.domain],
                tone: 'done',
            };
        // Everything below reduces to "nothing took effect", which the bare
        // chip already says. A conflict, a demotion to the profile, an
        // unroutable item, and an outright failure differ only in ways that
        // describe our pipeline, so none of them earns a line.
        case 'conflict_notified':
        case 'fallback_memory':
        case 'unroutable':
        case 'stale_no_op':
        case 'failed':
        case null:
        case undefined:
            return null;
        default:
            return null;
    }
}

/** Every outcome line for one note, in item order, skipping unresolved items. */
export function noteOutcomes(note: AgentNoteRead): Array<NoteOutcome> {
    if (!note.is_settled) {
        return [];
    }
    return note.items
        .map((item) => noteItemOutcome(note, item))
        .filter((outcome): outcome is NoteOutcome => outcome !== null);
}
